// BotCard.jsx
// src/components/bot-management/BotCard.jsx
import React, { useState, useRef, useEffect, useMemo} from 'react';
import { Check, Plus, Key, ChevronDown, Star, Bot, Code, Pencil, Settings } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useConversation } from '@/managers/ConvoManager';
import { botManager } from '@/managers/BotManager';
import { authManager } from '@/managers/AuthManager';
import { sortModelsWithPlatformPriority } from './BotCatalog';
import { useTheme } from '@/managers/ThemeProvider';
import PlatformTag from './PlatformTag';
/* ========================
 * useAuthSettings - Custom hook to access settings from AuthManager
 * Provides reactive access to settings stored in AuthManager
 * ======================== */
const useAuthSettings = () => {
  const [settings, setSettings] = useState(() => authManager.getSettings());

  useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setSettings(state.settings);
    });
    return unsubscribe;
  }, []);

  return {
    settings,
    starModel: (botId, modelId, starred) => authManager.starModel(botId, modelId, starred),
    getModelPreferencesForBot: (botId) => authManager.getModelPreferencesForBot(botId)
  };
};

const Tooltip = ({ children, content, position = 'top' }) => {
    const { isLightTheme } = useTheme();
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const targetRef = useRef(null);

    const showTooltip = () => {
        if (!targetRef.current) return;
        const rect = targetRef.current.getBoundingClientRect();
        const tooltipContent = content || '';
        const temp = document.createElement('div');
        temp.style.visibility = 'hidden';
        temp.style.position = 'absolute';
        temp.style.whiteSpace = 'nowrap';
        temp.className = `absolute px-2 py-1 rounded text-[9px] ${isLightTheme ? 'bg-surface-dark text-on-surface' : 'bg-surface-dark text-on-surface'}`;
        temp.textContent = tooltipContent;
        document.body.appendChild(temp);
        const tooltipWidth = temp.offsetWidth;
        document.body.removeChild(temp);
        let top, left;
        const padding = 8;
        const viewportPadding = 10;
        switch (position) {
            case 'top':
                top = rect.top - padding;
                left = Math.min(
                    Math.max(viewportPadding, rect.left + (rect.width / 2)),
                    window.innerWidth - tooltipWidth - viewportPadding
                );
                break;
            case 'right':
                top = rect.top + (rect.height / 2);
                left = rect.right + padding;
                if (left + tooltipWidth + viewportPadding > window.innerWidth) {
                    left = rect.left - padding - tooltipWidth;
                }
                break;
            case 'bottom':
                top = rect.bottom + padding;
                left = Math.min(
                    Math.max(viewportPadding, rect.left + (rect.width / 2)),
                    window.innerWidth - tooltipWidth - viewportPadding
                );
                break;
            case 'left':
                top = rect.top + (rect.height / 2);
                left = rect.left - padding - tooltipWidth;
                if (left < viewportPadding) {
                    left = rect.right + padding;
                }
                break;
            default:
                top = rect.top - padding;
                left = Math.min(
                    Math.max(viewportPadding, rect.left + (rect.width / 2)),
                    window.innerWidth - tooltipWidth - viewportPadding
                );
        }
        setCoords({ top, left });
        setVisible(true);
    };

    const hideTooltip = () => setVisible(false);

    useEffect(() => {
        const handleScroll = () => { if (visible) hideTooltip(); };
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [visible]);

    return (
        <>
            <span ref={targetRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip} className="relative inline-block">
                {children}
            </span>
            {visible && ReactDOM.createPortal(
                <div
                    ref={tooltipRef}
                    className={`absolute px-2 py-1 rounded opacity-90 shadow-lg pointer-events-none text-[9px] ${isLightTheme ? 'bg-surface-dark text-on-surface' : 'bg-surface-dark text-on-surface'}`}
                    style={{
                        position: 'fixed',
                        top: coords.top,
                        left: coords.left,
                        transform: 'translate(-50%, -100%)',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                    }}
                >
                    {content}
                </div>,
                document.body
            )}
        </>
    );
};

const ModelSelector = ({
    model,
    isActive,
    onClick,
    onKeyClick,
    onNewChat,
    availablePlatforms,
    botId,
    isStarred = false,
    onToggleStar
}) => {
    const { isLightTheme } = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const safePlatformKey = String(model.platform || '').toUpperCase();
    const hasKey = !!(availablePlatforms && typeof availablePlatforms === 'object' && availablePlatforms[safePlatformKey]);

    

const getBgColor = () => {
    const p = String(model.platform || '').toUpperCase();
    switch (p) {
        case 'LUCENA':
            return isLightTheme ? 'hover:bg-rose-500/15 active:bg-rose-500/20' : 'hover:bg-rose-500/10 active:bg-rose-500/15';
        case 'OPENAI': 
            return isLightTheme ? 'hover:bg-emerald-500/15 active:bg-emerald-500/20' : 'hover:bg-emerald-500/10 active:bg-emerald-500/15';
        case 'CLAUDE': 
            return isLightTheme ? 'hover:bg-[#F6A192]/15 active:bg-[#F6A192]/20' : 'hover:bg-[#F6A192]/10 active:bg-[#F6A192]/15';
        case 'GEMINI': 
            return isLightTheme ? 'hover:bg-violet-500/15 active:bg-violet-500/20' : 'hover:bg-violet-400/10 active:bg-violet-400/15';
        case 'MISTRAL': 
            return isLightTheme ? 'hover:bg-blue-500/15 active:bg-blue-500/20' : 'hover:bg-blue-400/10 active:bg-blue-400/15';
        case 'PALM': 
            return isLightTheme ? 'hover:bg-green-500/15 active:bg-green-500/20' : 'hover:bg-green-400/10 active:bg-green-400/15';
        case 'LLAMA': 
            return isLightTheme ? 'hover:bg-orange-500/15 active:bg-orange-500/20' : 'hover:bg-orange-400/10 active:bg-orange-400/15';
        default: 
            return isLightTheme ? 'hover:bg-background-dark' : 'hover:bg-surface-dark';
    }
};

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasKey) {
            onClick(model);
        }
    };

    const handleNewChatButtonClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (hasKey && onNewChat) {
            onNewChat(model);
        }
    };

    const handleToggleStar = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onToggleStar) {
            onToggleStar(model.id, !isStarred);
        }
    };

    const modelName = model.name || model.cleanModelName || model.model || 'Unknown Model';

    return (
        <div
            className={`
                relative w-full text-left p-2 rounded-sm
                transition-colors duration-200 group
                ${hasKey ? getBgColor() : (isLightTheme ? 'bg-background-dark' : 'bg-surface-dark')}
                ${isActive ? (isLightTheme ? 'bg-background-dark' : 'bg-surface-dark') : ''}
                ${hasKey ? 'cursor-pointer' : 'cursor-default'}
                ${!hasKey ? 'opacity-60' : ''}
            `}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex items-center gap-2">
                <PlatformTag
                    platform={model.platform}
                    hasKey={hasKey}
                />
                <span className={`text-[10px] flex items-center gap-1.5 flex-1 ${hasKey ? 'text-on-surface' : 'text-on-surface-dim'}`}>
                    {modelName}
                    {hasKey && onToggleStar && (
                        <button
                            onClick={handleToggleStar}
                            className={`transition-opacity duration-200 p-0.5 -m-0.5 rounded ${
                                isStarred ? 'opacity-100' : isHovered ? 'opacity-100' : 'opacity-0'
                            }`}
                            title={isStarred ? "Unstar model" : "Star model"}
                        >
                            <Star
                                size={8}
                                className={`transition-colors duration-200 ${
                                    isStarred
                                        ? `fill-yellow-${isLightTheme ? '500' : '400'} text-yellow-${isLightTheme ? '500' : '400'}`
                                        : `text-on-surface-dim hover:text-yellow-${isLightTheme ? '500' : '400'}`
                                }`}
                            />
                        </button>
                    )}
                    {!hasKey && (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                onKeyClick?.(model.platform);
                            }}
                            className={`group/key flex items-center justify-center w-3.5 h-3.5 rounded-full relative cursor-pointer ${isLightTheme ? 'bg-background-dark' : 'bg-surface-dark'}`}
                        >
                            <Key size={8} className="text-on-surface-dim group-hover/key:text-primary" />
                        </span>
                    )}
                </span>
            </div>

            {isActive && (
                <>
                    <div className="absolute right-8 top-2.5 flex items-center">
                        <button
                            onClick={handleNewChatButtonClick}
                            className={`
                                flex items-center
                                ${hasKey ? 'text-primary hover:text-primary/80' : 'text-on-surface-dim'}
                                ${!hasKey && 'pointer-events-none'}
                            `}
                        >
                            <Plus size={12} className="mr-1" />
                            <span className="text-xs">New Chat</span>
                        </button>
                    </div>
                    <Check size={12} className="absolute right-2 top-2.5 text-primary" />
                </>
            )}
        </div>
    );
};

const BotIcon = ({ category, isFavorite, onToggleFavorite, showStar, isDefault, isVanillaBot }) => {
    const { isLightTheme } = useTheme();
    const Icon = category === 'code' ? Code :
        category === 'writing' ? Pencil : Bot;

    const handleToggleFavorite = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onToggleFavorite) {
            onToggleFavorite();
        }
    };

    return (
        <div className="relative group">
            <Icon className={`w-3.5 h-3.5 transition-colors duration-200 ${
                isFavorite || isDefault ? 'text-primary' :
                isVanillaBot ? 'text-primary/70' : 'text-on-surface-dim'
            }`} />
            {onToggleFavorite && (
                <button
                    type="button"
                    onClick={handleToggleFavorite}
                    className={`absolute -top-1 -right-1 transition-opacity duration-200 ${showStar || isFavorite ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100`}
                >
                    <Tooltip content={isFavorite ? "Unfavorite" : "Favorite"} position="right">
                        <Star size={8} className={`transition-colors duration-200 ${isFavorite ? 'fill-primary text-primary' : 'text-on-surface-dim hover:text-primary'}`} />
                    </Tooltip>
                </button>
            )}
        </div>
    );
};

const BotCard = ({
    bot,
    onSelectBot,
    onSelectModel,
    onToggleFavorite,
    isFavorite,
    availablePlatforms,
    onOpenKeys,
    createNewChat,
    onOpenCatalog,
    isActive,
    defaultExpanded = false
}) => {
    const { isLightTheme } = useTheme();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [isCardHovered, setIsCardHovered] = useState(false);
    const { currentConversation } = useConversation();
    const { starModel, getModelPreferencesForBot, settings } = useAuthSettings();
    const descriptionRef = useRef(null);
    const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
    const descriptionHoverTimeout = useRef(null);

    const modelPreferences = useMemo(() => getModelPreferencesForBot(bot.id), [
        getModelPreferencesForBot, 
        bot.id, 
        settings.zPreferences?.botModelPreferences?.modelPreferences
    ]);
    const { starredModels = [], hiddenModels = [] } = modelPreferences;

    const allBotModels = useMemo(() => {
        return botManager.getAvailableModelsForBot(bot.id);
    }, [bot.id, settings, botManager.getState().allPlatformModels, botManager.getState().platformModelsWithKeys]);
    
    const modelsToDisplay = React.useMemo(() => {
        const visibleModels = allBotModels.filter(model => !hiddenModels.includes(model.id));
        return sortModelsWithPlatformPriority(visibleModels, starredModels, availablePlatforms);
    }, [allBotModels, hiddenModels, starredModels, availablePlatforms]);

    useEffect(() => {
        if (isActive) {
            setIsExpanded(true);
        }
    }, [isActive]);

    useEffect(() => {
        setIsExpanded(defaultExpanded);
    }, [defaultExpanded]);

    useEffect(() => {
        if (descriptionRef.current) {
            setIsDescriptionTruncated(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
        }
    }, [isExpanded, bot.description]);

    const handleDescriptionMouseEnter = () => {
        descriptionHoverTimeout.current = setTimeout(() => {
            setIsCardHovered(true);
        }, 300);
    };

    const handleDescriptionMouseLeave = () => {
        clearTimeout(descriptionHoverTimeout.current);
        setIsCardHovered(false);
    };

    const handleToggleModelStar = (modelId, shouldStar) => {
        starModel(bot.id, modelId, shouldStar);
    };

    const isVanillaBot = bot.id === 'vanillabot';

    const renderPlatformTags = () => {
        const platforms = modelsToDisplay.map(model => String(model.platform || '').toUpperCase());
        const uniquePlatforms = [...new Set(platforms)].filter(p => p);
        const hasKey = (platform) => !!(availablePlatforms && availablePlatforms[platform]);

        const maxTagsToShow = 4;
        const visiblePlatforms = uniquePlatforms.slice(0, maxTagsToShow);
        const overflowPlatforms = uniquePlatforms.slice(maxTagsToShow);

        return (
            <div className="flex gap-1.5 items-center">
                {visiblePlatforms.map(platform => (
                    <div key={platform} className="transition-all duration-200 ease-in-out" style={{ transform: 'scale(0.95)' }}>
                        <PlatformTag
                            platform={platform}
                            hasKey={hasKey(platform)}
                            compact={true}
                        />
                    </div>
                ))}
                {overflowPlatforms.length > 0 && (
                    <Tooltip
                        content={overflowPlatforms.map(p => (
                            <div key={p}>
                                <PlatformTag platform={p} hasKey={hasKey(p)} compact={false} />
                            </div>
                        ))}
                        position="top"
                    >
                        <span className={`px-1 text-[10px] rounded cursor-default ${isLightTheme ? 'bg-background-dark text-on-surface/70' : 'bg-surface-dark text-on-surface/70'}`} style={{ minWidth: 18 }}>
                            +{overflowPlatforms.length}
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    };

    // Truncate bot name to 18 characters
    const truncatedBotName = bot.name || '';

    return (
        <div
            className={`border-b border-border-subtle last:border-0 ${
                isActive ? (isLightTheme ? 'bg-background-dark/60' : 'bg-surface-dark/40') : ''
            } ${isVanillaBot ? 'border-dashed border-primary/30' : ''}`}
            onMouseEnter={() => setIsCardHovered(true)}
            onMouseLeave={() => setIsCardHovered(false)}
        >
            <div className="flex w-full">
                <div
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`flex-1 min-w-0 px-3 py-2 flex items-center gap-2.5 transition-colors duration-200 cursor-pointer relative
                        ${isLightTheme ? 'hover:bg-background-dark/60' : 'hover:bg-surface-dark/60'}
                        ${isActive ? (isLightTheme ? 'bg-background-dark/60' : 'bg-surface-dark/60') : ''}`}
                >
                    <BotIcon
                        category={bot.category}
                        isFavorite={isFavorite} // This determines the star's filled state
                        isDefault={bot.isDefault} // This influences the icon color
                        isVanillaBot={isVanillaBot}
                        onToggleFavorite={onToggleFavorite} // CORRECTED LINE
                        showStar={isCardHovered}
                    />
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <span className={`text-xs font-medium truncate block ${
                            isVanillaBot ? 'text-primary/90' : 'text-on-surface'
                        }`} title={bot.name}>
                            {truncatedBotName}{isVanillaBot && <span className="text-[10px] text-primary/60 ml-1">(Base)</span>}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {!isExpanded && renderPlatformTags()}
                        {onOpenCatalog && (
                            <Tooltip content="Bot Settings & Catalog" position="top">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenCatalog(bot);
                                    }}
                                    className={`p-1 -m-1 rounded transition-colors ${isLightTheme ? 'hover:bg-on-surface/10' : 'hover:bg-on-surface/10'}`}
                                    aria-label="Open Bot Settings and Catalog"
                                >
                                    <Settings
                                        size={14}
                                        className="text-on-surface-dim group-hover:text-primary"
                                    />
                                </button>
                            </Tooltip>
                        )}
                        <ChevronDown
                            size={14}
                            className={`text-on-surface-dim transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                    </div>
                </div>
            </div>
            {isExpanded && (
                <div className="py-1 px-2 grid gap-0.5">
                    {bot.description && (
                        <div className="relative px-2 py-1 group"
                              onMouseEnter={handleDescriptionMouseEnter}
                              onMouseLeave={handleDescriptionMouseLeave}>
                            <p ref={descriptionRef} className="text-[11px] text-on-surface-dim line-clamp-3">
                                {bot.description}
                            </p>
                            {isCardHovered && isDescriptionTruncated && (
                                <div className={`absolute text-[11px] px-2 py-1 rounded shadow-lg pointer-events-none z-10 top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-normal max-w-xs ${isLightTheme ? 'bg-surface-dark text-on-surface' : 'bg-surface-dark text-on-surface'}`}>
                                    {bot.description}
                                </div>
                            )}
                        </div>
                    )}

                    {modelsToDisplay.map((model) => (
                        <ModelSelector
                            key={model.id}
                            model={model}
                            botId={bot.id}
                            isActive={
                                currentConversation?.botId === bot.id &&
                                currentConversation?.modelId === model.id
                            }
                            isStarred={starredModels.includes(model.id)}
                            onClick={() => onSelectModel(bot, model)}
                            onNewChat={() => createNewChat(bot, model)}
                            onToggleStar={handleToggleModelStar}
                            availablePlatforms={availablePlatforms}
                            onKeyClick={onOpenKeys}
                        />
                    ))}

                    {modelsToDisplay.length === 0 && (
                        <div className="px-2 py-3 text-center">
                            <p className="text-xs text-on-surface-dim">No models available for this bot.</p>
                            {onOpenKeys && (
                                <button
                                    onClick={() => onOpenKeys()}
                                    className="mt-2 text-xs text-primary hover:text-primary/80 flex items-center justify-center mx-auto"
                                >
                                    <Key size={10} className="mr-1" />
                                    Add API Keys
                                </button>
                             )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BotCard;